import { BaseEntity, EntityContext, ExportKind, getPropertyDefaultValue, ORMError } from "@archipad-js/core/orm";
import {
    EntityOperation,
    EntityOperations,
    EntityUpdate,
    NetPatch,
    NetPatchAttachmentManifest,
    NetPatchData,
    NetPatchHeader,
    NetPatchOperations,
    Patch,
    PatchHeader,
} from "./patch.types";
import {
    MutablePatch,
    MutableEntityOperations,
    MutableEntityOperation,
    MutableAttachmentUpdate,
    MutableEntityDelete,
    MutableEntityCreate,
    MutableEntityUpdate,
    MutableAttachmentDelete,
    MutablePatchHeader,
} from "./mutablePatch";
import { IllegalArgumentError, NotImplementedError, PatchError } from "@archipad-js/core/error";
import { AttachmentSerializer, SerializedJSONAttachment } from "./attachmentSerializer";
import { Logger, LoggerManager } from "@archipad-js/core/logger";
import { FileDescriptor, isPlainObject } from "@archipad-js/core/utils";
import { parseDate } from "./patch.helper";

class DummyAttachmentSerializer {
    private _dummyAttachment = { url: "" };

    serializeAttachment(file: FileDescriptor, entityId: string, attachmentKey: string): SerializedJSONAttachment {
        return this._dummyAttachment;
    }
    deserializeAttachment(json: SerializedJSONAttachment, entityId: string, attachmentKey: string): FileDescriptor {
        throw new NotImplementedError("deserializeAttachment", "DummyAttachmentSerializer");
    }
}

export class PatchService {
    constructor(protected readonly logger: Logger = LoggerManager.getLogger("PatchService")) {}

    /**
     * Apply the changes in a patch in an entity context
     *
     * @param entityContext the entity context
     * @param patch the patch to apply
     */
    applyPatch(entityContext: EntityContext, patch: Patch): void {
        const entityTypes = entityContext.entityTypes;

        entityContext.ensureModelMinVersion(patch.header.modelMinVersion);

        for (const entityType of entityTypes.byIndex) {
            if (!entityType.import) continue; // non importable entities

            const entityName = entityType.entityName;
            const operations = patch.operationsByType.get(entityName);
            if (!operations) continue;
            operations.operationById.forEach((operation, entityId) => {
                switch (operation.type) {
                    case "delete":
                        {
                            // delete the entity
                            const object = entityContext.getEntity(entityName, entityId);
                            if (object) entityContext.deleteEntity(object);
                        }
                        break;

                    case "create":
                    case "update":
                        {
                            let object = entityContext.getEntity(entityName, entityId);
                            if (!object) {
                                object = entityContext.createEntity(entityName, entityId);
                                if (!object) throw new ORMError('Cannot create entity of type "' + entityName + '"');
                            } else {
                                // reset transient properties to their default values (used for properties that are reset after a push)
                                for (const k in entityType.metadata) {
                                    const property = entityType.metadata[k];
                                    if (property.relation === false && property.transient)
                                        object[k] = getPropertyDefaultValue(property);
                                }
                            }

                            // apply patch operation
                            operation.propertyUpdates.forEach((val, k) => {
                                const property = entityType.metadata[k];
                                if (!property) return; // no such property, ignore

                                if (property.calculated) {
                                    return;
                                }

                                // the temp patch ignores and forces import and export flags
                                if (!(property.importable || patch.header.temp)) return; // ignore property

                                if (property.relation === true) {
                                    const relation = property;
                                    if (!entityTypes.byName[relation.target.entityName]) return;

                                    if (val === null) {
                                        object[k] = null;
                                    } else {
                                        const target = entityContext.getEntity(relation.target.entityName, val);
                                        if (!target) {
                                            this.logger.warn(
                                                `missing entity reference "${object.entityType.entityName}.${k}" to "${relation.target.entityName}(${val})"`,
                                            );
                                        } else {
                                            object[k] = target;
                                        }
                                    }
                                } else {
                                    if (property.primaryKey) return;

                                    if (val === null) {
                                        object[k] = null;
                                        return;
                                    }

                                    // ensure that type of the value is the expected one
                                    switch (property.type) {
                                        case "number":
                                            {
                                                const v = Number(val);
                                                if (!isNaN(v)) {
                                                    object[k] = v;
                                                } else {
                                                    this.logger.warn("** Bad number value in patch", val);
                                                    object[k] = null;
                                                }
                                            }
                                            break;
                                        case "date":
                                            {
                                                object[k] = parseDate(val);
                                            }
                                            break;
                                        case "string":
                                            {
                                                object[k] = String(val);
                                            }
                                            break;
                                        case "boolean":
                                            {
                                                object[k] = val ? true : false;
                                            }
                                            break;
                                        default:
                                            {
                                                object[k] = val;
                                            }
                                            break;
                                    }
                                }
                            });

                            // apply attachment operations
                            operation.attachmentUpdates.forEach((attachmentUpdate, attachmentId) => {
                                if (attachmentUpdate.deleted === true) {
                                    object.setAttachmentForKey(attachmentId, null);
                                } else {
                                    object.setAttachmentForKey(attachmentId, attachmentUpdate.file);
                                }
                            });
                        }
                        break;
                }
            });
        }
    }

    /**
     * Extract all entities attachments from the entity context into a AttachmentManifest
     */
    extractDumpManifest(
        entityContext: EntityContext,
        attachmentSerializer: AttachmentSerializer,
    ): NetPatchAttachmentManifest {
        const attachmentManifest = {};
        const entityTypes = entityContext.entityTypes;
        for (const entityType of entityTypes.byIndex) {
            const entityName = entityType.entityName as keyof EntityTypeMap;

            for (const entityId in entityContext.entitiesByType[entityName]) {
                const entity: BaseEntity = entityContext.getEntity(entityName, entityId);
                const attachments = entity.getAttachments();
                if (Object.keys(attachments).length) {
                    attachmentManifest[entityId] = {};
                }
                for (const attachmentKey in attachments) {
                    const attachment = attachments[attachmentKey];
                    attachmentManifest[entityId][attachmentKey] = attachmentSerializer.serializeAttachment(
                        attachment,
                        entityId,
                        attachmentKey,
                    );
                }
            }
        }
        return attachmentManifest;
    }
    /**
     * Substract from a patch another compatible patch
     *
     * @param entityContext the entity context
     * @param patch the patch to modify
     * @param patch2 the patch to substract
     */
    minusPatch(entityContext: EntityContext, patch: MutablePatch, patch2: Patch): void {
        patch.operationsByType.forEach((unused: MutableEntityOperations, entityName: string) => {
            const operations2: EntityOperations = patch2.operationsByType.get(entityName);
            if (operations2 == null || operations2.operationById.size == 0) return;
            const operations: MutableEntityOperations = patch.operationsByType.get(entityName);

            operations.operationById.forEach((unused: MutableEntityOperation, entityId: string) => {
                const operation: MutableEntityOperation = operations.operationById.get(entityId);
                const operation2: EntityOperation = operations2.operationById.get(entityId);

                if (operation2 == null) return;

                if (operation.type != operation2.type) throw new PatchError("incompatible operands");

                if (operation.type === "delete") {
                    operations.operationById.delete(entityId);
                } else if (operation.type === "create") {
                    // TODO check each property value
                    operations.operationById.delete(entityId);
                } else if (operation.type === "update") {
                    // TODO check each property value
                    const operation2U: EntityUpdate = operation2 as EntityUpdate;
                    operation.propertyUpdates.forEach((unused: any, propertyName: string) => {
                        if (!operation2U.propertyUpdates.has(propertyName)) return;
                        operation.propertyUpdates.delete(propertyName);
                    });
                    operation.attachmentUpdates.forEach((unused: MutableAttachmentUpdate, attachmentKey: string) => {
                        if (!operation2U.attachmentUpdates.has(attachmentKey)) return;
                        operation.attachmentUpdates.delete(attachmentKey);
                    });
                    if (operation.propertyUpdates.size == 0 && operation.attachmentUpdates.size == 0) {
                        operations.operationById.delete(entityId);
                    }
                }
            });
        });
    }
    /**
     * Reset the ORM commit of a patch
     *
     * @param entityContext the entity context
     * @param patch the patch
     */
    resetCommitPatch(entityContext: EntityContext, patch: Patch): void {
        patch.operationsByType.forEach((operations: EntityOperations, entityName: string) => {
            operations.operationById.forEach((operation: EntityOperation, entityId: string) => {
                if (operation.type === "delete") {
                    const entity = entityContext.getEntity(entityName, entityId);
                    if (entity != null) throw new PatchError("cannot reset commit entity delete : entity exists");
                    entityContext["_deletedEntities"][entityName][entityId] = true;
                } else if (operation.type === "create") {
                    const entity = entityContext.getEntity(entityName, entityId);
                    if (entity == null)
                        throw new PatchError("cannot reset commit entity create : entity does not exist");
                    if (operation.attachmentUpdates.size != 0)
                        throw new PatchError(
                            "cannot reset commit entity create : entity has attachments : not supported",
                        );
                    entityContext["_createdEntities"][entityName][entityId] = entity;
                } else if (operation.type === "update") {
                    throw new PatchError("cannot reset commit entity update : not supported");
                }
            });
        });
    }
    /**
     * Extract the changes from the entity context to a patch
     *
     * @param entityContext the entity context
     * @param patchHeader the patch header
     * @return the new patch
     */
    extractPatch(entityContext: EntityContext, patchHeader: PatchHeader): MutablePatch {
        const patch = new MutablePatch(patchHeader);

        const entityTypes = entityContext.entityTypes;
        for (const entityType of entityTypes.byIndex) {
            const entityName = entityType.entityName;

            let operations = patch.operationsByType.get(entityName);
            if (!operations) {
                operations = new MutableEntityOperations();
                patch.operationsByType.set(entityName, operations);
            }

            const metadata = entityType.metadata;

            // deletions
            const deletedEntitites = entityContext.deletedEntities[entityName];
            for (const entityId in deletedEntitites) operations.operationById.set(entityId, new MutableEntityDelete());

            // creations
            const createdEntities = entityContext.createdEntities[entityName];
            for (const entityId in createdEntities) {
                const operation = new MutableEntityCreate();
                const entity = createdEntities[entityId];

                for (const name in metadata) {
                    const property = metadata[name];
                    if (property.calculated) {
                        continue;
                    }

                    const val = entity[name];

                    if (val === undefined) {
                        throw new ORMError(
                            `Illegal attempt to create an entity "${entityName}" with a property "${property.name}" set to 'undefined'.`,
                        );
                    }

                    if (property.relation) {
                        const relation = property;
                        if (!relation.single) continue; // only single relationship are supported
                        operation.propertyUpdates.set(name, val ? val.id : null);
                    } else if (!property.relation) {
                        operation.propertyUpdates.set(name, val);
                    }
                }

                operations.operationById.set(entityId, operation);
            }

            // modifications
            const modifiedEntitites = entityContext.modifiedEntities[entityName];
            for (const entityId in modifiedEntitites) {
                const originalValues = modifiedEntitites[entityId];
                const currentEntity = entityContext.getEntity(entityName, entityId);

                const operation = new MutableEntityUpdate();
                for (const name in metadata) {
                    const property = metadata[name];
                    if (property.calculated) {
                        continue;
                    }
                    if (
                        !(
                            property.exportable ||
                            (patchHeader.temp && property.export != ExportKind.cache && !property.relation)
                        )
                    )
                        continue;
                    if (!(name in originalValues)) continue;

                    const newValue = currentEntity[name];
                    const oldValue = originalValues[name];

                    if (newValue === undefined) {
                        throw new ORMError(
                            `Illegal attempt to update an entity "${entityName}" with a property "${property.name}" set to 'undefined'.`,
                        );
                    }

                    if (property.relation === true) {
                        const relation = property;
                        if (!relation.single) continue; // only single relationship are supported
                        if (oldValue !== newValue) operation.propertyUpdates.set(name, newValue ? newValue.id : null);
                    } else {
                        if (oldValue !== newValue) {
                            // in case of date we when to compare timestamp
                            if (
                                property.type !== "date" ||
                                !oldValue ||
                                !newValue ||
                                (oldValue as Date).getTime() !== (newValue as Date).getTime()
                            ) {
                                operation.propertyUpdates.set(name, currentEntity[name]);
                            }
                        }
                    }
                }

                operations.operationById.set(entityId, operation);
            }

            // attachments
            const createdAttachments = entityContext.createdAttachments[entityName];
            for (const entityId in createdAttachments) {
                const operation = operations.operationById.get(entityId);
                if (!operation || operation.type === "delete") continue;
                const attachments = createdAttachments[entityId];
                for (const attachmentKey in attachments) {
                    const attachment = attachments[attachmentKey];
                    operation.attachmentUpdates.set(attachmentKey, new MutableAttachmentUpdate(attachment));
                }
            }
            const updatedAttachments = entityContext.updatedAttachments[entityName];
            for (const entityId in updatedAttachments) {
                const operation = operations.operationById.get(entityId);
                if (!operation || operation.type === "delete") continue;
                const attachments = updatedAttachments[entityId];
                for (const attachmentKey in attachments) {
                    const attachment = attachments[attachmentKey];
                    if (!attachment) operation.attachmentUpdates.set(attachmentKey, new MutableAttachmentDelete());
                    else operation.attachmentUpdates.set(attachmentKey, new MutableAttachmentUpdate(attachment));
                }
            }

            // sanity check on modifications
            operations.operationById.forEach(function (operation: MutableEntityOperation, entityId: string) {
                if (
                    operation.type === "update" &&
                    operation.propertyUpdates.size == 0 &&
                    operation.attachmentUpdates.size == 0
                ) {
                    operations.operationById.delete(entityId);
                }
            });
        }

        return patch;
    }

    /**
     * Extract all entities and attachments from the entity context into a patch
     *
     * @param entityContext - the entity context
     * @param patchHeader - the patch header
     * @return the dump patch
     */
    extractDumpPatch(entityContext: EntityContext, patchHeader: PatchHeader): MutablePatch {
        const patch = new MutablePatch(patchHeader);

        for (const entityType of entityContext.entityTypes.byIndex) {
            const metadata = entityType.metadata;
            const entityName = entityType.entityName;

            let operations = patch.operationsByType.get(entityName);
            if (!operations) {
                operations = new MutableEntityOperations();
                patch.operationsByType.set(entityName, operations);
            }

            const entities = entityContext.entitiesByType[entityName];

            for (const entityId in entities) {
                const entity = entities[entityId];

                const operation = new MutableEntityCreate();

                for (const name in metadata) {
                    const property = metadata[name];
                    if (property.calculated) {
                        continue;
                    }

                    const val = entity[name];

                    if (val === undefined) {
                        throw new ORMError(
                            `Illegal attempt to create an entity "${entityName}" with a property "${property.name}" set to 'undefined'.`,
                        );
                    }

                    // ignore non exportable properties
                    if (!property.exportable) {
                        continue;
                    }

                    if (property.relation) {
                        const relation = property;

                        if (!relation.single) {
                            continue; // only single relationship are supported
                        }

                        operation.propertyUpdates.set(name, val ? val.id : null);
                    } else {
                        operation.propertyUpdates.set(name, val);
                    }
                }

                const attachments = entity.getAttachments();

                for (const attachmentKey in attachments) {
                    const attachment = attachments[attachmentKey];
                    operation.attachmentUpdates.set(attachmentKey, new MutableAttachmentUpdate(attachment));
                }

                operations.operationById.set(entityId, operation);
            }
        }

        return patch;
    }

    /**
     * Export a patch to network format (a JSON serializable structure)
     *
     * If the attachmentSerializer then
     *
     * @param entityContext the orm context
     * @param attachmentSerializer attachment serializer to use for attachments
     * @param patch the patch to export
     * @return the patch and the attachment manifest in network representation
     */
    exportPatch(
        entityContext: EntityContext,
        attachmentSerializer: AttachmentSerializer,
        patch: MutablePatch,
    ): NetPatch | null {
        if (!patch) return null;

        const header: NetPatchHeader = {
            diff: true,
            minVersion: patch.header.modelMinVersion,
            version: patch.header.modelVersion,
        };

        if (patch.header.isDump) {
            header.diff = false;
        }

        if (patch.header.clientName) header.client = patch.header.clientName;
        if (patch.header.user) header.user = patch.header.user;

        if (patch.header.temp) {
            header.temp = true;
            header.tempData = {
                emptyLike: patch.header.emptyLike,
            };
        }

        const patchData: [NetPatchHeader, ...Array<NetPatchOperations>] = [header];
        const attachmentManifest = {};

        const entityTypes = entityContext.entityTypes;
        entityTypes.byIndex.forEach((entityType) => {
            if (!entityType.export) return; // non exportable entities

            const entityName = entityType.entityName;
            const metadata = entityType.metadata;
            const patchOperations: NetPatchOperations = [entityName];

            const operations = patch.operationsByType.get(entityName);
            if (!operations || operations.operationById.size === 0) return;

            const markedOperations: Set<string> = new Set();

            function processOperation(operation: EntityOperation, entityId: string) {
                if (entityId === null || markedOperations.has(entityId)) {
                    return;
                }

                switch (operation.type) {
                    case "create":
                    case "update":
                        {
                            const patchOperation = {
                                id: entityId,
                            };

                            if (operation.type === "update") {
                                patchOperation["_metadata"] = { op: "update" };
                            }

                            // for each property to update
                            operation.propertyUpdates.forEach((propertyValue, propertyName) => {
                                const property = metadata[propertyName];

                                // the temp patch ignores and forces import and export flags
                                if (
                                    !(property.exportable || (patch.header.temp && property.export != ExportKind.cache))
                                )
                                    return; // skip property

                                // the order of EntityOperations is not guaranteed so this code is required to make sure parent Remarks are created before they are referenced by child remarks.
                                if (
                                    propertyValue &&
                                    property.relation &&
                                    property.single &&
                                    property.target.entityName === entityName
                                ) {
                                    // - if the updated property is a relation that points to the same entity type (e.g: a Remark that references another Remark through parentRemark)
                                    // - if there is an operation on the target entity in the same patch
                                    // then put the operation on the target entity first in the patch.
                                    const operation = operations.operationById.get(propertyValue);
                                    if (operation) processOperation(operation, propertyValue);
                                }

                                patchOperation[propertyName] = propertyValue;
                            });

                            // add attachment updates
                            if (operation.attachmentUpdates.size !== 0) {
                                attachmentManifest[entityId] = {};
                            }

                            operation.attachmentUpdates.forEach((update, key) => {
                                if (update.deleted === true) {
                                    attachmentManifest[entityId][key] = { deleted: true };
                                } else {
                                    attachmentManifest[entityId][key] = attachmentSerializer.serializeAttachment(
                                        update.file,
                                        entityId,
                                        key,
                                    );
                                }
                            });

                            if (operation.propertyUpdates.size !== 0 || operation.attachmentUpdates.size !== 0) {
                                patchOperations.push(patchOperation);
                            }
                        }
                        break;
                    case "delete":
                        {
                            patchOperations.push({
                                id: entityId,
                                _metadata: { op: "delete" },
                            });
                        }
                        break;
                }

                markedOperations.add(entityId);
            }
            operations.operationById.forEach(processOperation);

            if (patchOperations.length > 1) patchData.push(patchOperations);
        });

        if (patchData.length === 1) return null;
        return { patch: patchData, attachmentManifest: attachmentManifest };
    }

    /**
     * Import a patch from network format
     *
     * @param entityContext the entity context
     * @param attachmentSerializer attachment serializer to use for attachments
     * @param patchData the patch data in network format
     * @param attachmentManifest the attachment manifest in network format
     * @return the patch
     */
    importPatch(
        entityContext: EntityContext,
        attachmentSerializer: AttachmentSerializer,
        patchData: NetPatchData,
        attachmentManifest?: NetPatchAttachmentManifest,
    ): MutablePatch {
        if (!this.isNetPatchData(patchData)) {
            throw new IllegalArgumentError("Bad patch data");
        }

        const patchHeader = patchData[0];
        if (patchHeader["diff"] !== true || !patchHeader["minVersion"] || !patchHeader["version"]) {
            this.logger?.warn("Bad patch header, autofixing", patchHeader);
            if (patchHeader["diff"] !== true) patchHeader["diff"] = true;
            if (!patchHeader["minVersion"]) patchHeader["minVersion"] = entityContext.modelMinVersion;
            if (!patchHeader["version"]) patchHeader["version"] = entityContext.modelVersion;
        }

        const header = new MutablePatchHeader();
        header.modelMinVersion = patchHeader["minVersion"];
        header.modelVersion = patchHeader["version"];
        header.clientName = patchHeader["clientName"];
        header.user = Object.assign({}, patchHeader["user"]);
        header.temp = !!patchHeader["temp"];
        header.emptyLike = false;

        const patch = new MutablePatch(header);

        for (let i = 1; i < patchData.length; i++) {
            const patchOperations = patchData[i];
            if (
                !Array.isArray(patchOperations) ||
                patchOperations.length === 0 ||
                typeof patchOperations[0] !== "string"
            )
                throw new PatchError("Bad patch operation list");

            const entityName = patchOperations[0];

            let operations = patch.operationsByType.get(entityName);
            if (!operations) {
                operations = new MutableEntityOperations();
                patch.operationsByType.set(entityName, operations);
            }

            for (let j = 1; j < patchOperations.length; j++) {
                const patchOperation = patchOperations[j];
                if (typeof patchOperation !== "object" || typeof patchOperation["id"] !== "string")
                    throw new PatchError("Bad patch operation");

                const entityId = patchOperation["id"];
                let isCreate = true;

                if (patchOperation._metadata) {
                    if (patchOperation._metadata["op"] === "delete") {
                        operations.operationById.set(entityId, new MutableEntityDelete());
                        continue;
                    } else if (patchOperation._metadata["op"] === "update") {
                        isCreate = false;
                    }
                }

                const operation = isCreate ? new MutableEntityCreate() : new MutableEntityUpdate();
                for (const propertyName in patchOperation) {
                    if (propertyName === "id" || propertyName === "_metadata") continue;
                    operation.propertyUpdates.set(propertyName, patchOperation[propertyName]);
                }

                if (attachmentManifest) {
                    const entityAttachments = attachmentManifest[entityId];
                    if (entityAttachments) {
                        for (const attachmentKey in entityAttachments) {
                            const attachment = entityAttachments[attachmentKey];

                            if (attachment.deleted === true) {
                                operation.attachmentUpdates.set(
                                    attachmentKey.normalize("NFD"),
                                    new MutableAttachmentDelete(),
                                );
                            } else {
                                // normalize the key and the attachment url to UTF-8 form D in patches because
                                // archipad sometimes writes the manifest in form C and stores files
                                // in form D on S3. (maps created from a photo).
                                if (attachment.url) attachment.url = attachment.url.normalize("NFD");

                                const attachmentFile = attachmentSerializer.deserializeAttachment(
                                    attachment,
                                    entityId,
                                    attachmentKey,
                                );
                                operation.attachmentUpdates.set(
                                    attachmentKey.normalize("NFD"),
                                    new MutableAttachmentUpdate(attachmentFile),
                                );
                            }
                        }
                    }
                }

                operations.operationById.set(entityId, operation);
            }
        }

        return patch;
    }

    /**
     * Shallow checks the given {@link NetPatchData} to establish if its a valid one
     * or not.
     */
    isNetPatchData(netPatchData: unknown): netPatchData is NetPatchData {
        if (!Array.isArray(netPatchData)) {
            return false;
        }

        if (netPatchData.length === 0) {
            return false;
        }

        const patchHeader = netPatchData[0] as unknown;
        if (!isPlainObject(patchHeader)) {
            return false;
        }

        const firstNetPatchOperations = netPatchData[1] as unknown;
        if (!Array.isArray(firstNetPatchOperations)) {
            return false;
        }

        const firstEntityName = firstNetPatchOperations[0];
        if (typeof firstEntityName !== "string") {
            return false;
        }

        const firstEntityOperations = firstNetPatchOperations[1];
        if (!isPlainObject(firstEntityOperations) || !("id" in firstEntityOperations)) {
            return false;
        }

        return true;
    }

    isEmpty(entityContext: EntityContext, mutablePatch: MutablePatch): boolean {
        let empty = true;
        mutablePatch.operationsByType.forEach(function (operations, entityName) {
            if (operations.operationById.size !== 0) empty = false;
        });
        // TODO remove me when this operation works safely
        // sanity check
        if (!empty && this.exportPatch(entityContext, new DummyAttachmentSerializer(), mutablePatch) == null) {
            empty = true;
            this.logger.error("fix errorneous check 'isEmpty'");
        }

        return empty;
    }
}
